import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Video, Save, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminLayout from '@/components/admin/AdminLayout';
import FileUpload from '@/components/admin/FileUpload';
import { useSite } from '@/context/SiteContext';
import { toast } from 'sonner';

const emptyVideo = {
  title: '',
  description: '',
  video_url: '',
  video_file: '',
  thumbnail: '',
  duration: ''
};

const AdminVideos = () => {
  const { videos, createVideo, updateVideo, deleteVideo, reorderVideos } = useSite();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [form, setForm] = useState(emptyVideo);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Move video up or down
  const moveVideo = async (videoId, direction) => {
    const currentIds = videos.map(v => v.id);
    const currentIndex = currentIds.indexOf(videoId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= currentIds.length) return;
    
    const newIds = [...currentIds];
    [newIds[currentIndex], newIds[newIndex]] = [newIds[newIndex], newIds[currentIndex]];
    
    setReordering(true);
    try {
      const success = await reorderVideos(newIds);
      if (success) {
        toast.success('Video order updated');
      } else {
        toast.error('Failed to update order');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to update order');
    } finally {
      setReordering(false);
    }
  };

  const openCreate = () => {
    setSelectedVideo(null);
    setForm(emptyVideo);
    setDialogOpen(true);
  };

  const openEdit = (video) => {
    setSelectedVideo(video);
    setForm({
      title: video.title,
      description: video.description || '',
      video_url: video.video_url || '',
      video_file: video.video_file || '',
      thumbnail: video.thumbnail || '',
      duration: video.duration || ''
    });
    setDialogOpen(true);
  };

  const openDelete = (video) => {
    setSelectedVideo(video);
    setDeleteDialogOpen(true);
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleFileChange = (field) => (url) => {
    setForm(prev => ({ ...prev, [field]: url }));
  };

  // Handle video upload with auto-generated thumbnail
  const handleVideoUploadComplete = (data) => {
    if (data.thumbnail) {
      // Store relative URL for domain independence
      setForm(prev => ({ ...prev, thumbnail: data.thumbnail }));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    
    if (selectedVideo) {
      const success = await updateVideo(selectedVideo.id, form);
      if (success) {
        toast.success('Video updated');
        setDialogOpen(false);
      } else {
        toast.error('Failed to update video');
      }
    } else {
      const result = await createVideo(form);
      if (result) {
        toast.success('Video added');
        setDialogOpen(false);
      } else {
        toast.error('Failed to add video');
      }
    }
    
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!selectedVideo) return;
    
    const success = await deleteVideo(selectedVideo.id);
    if (success) {
      toast.success('Video deleted');
    } else {
      toast.error('Failed to delete video');
    }
    setDeleteDialogOpen(false);
  };

  // Get video source (prefer uploaded file over URL)
  const getVideoSource = (video) => {
    return video.video_file || video.video_url || '';
  };

  return (
    <AdminLayout title="Videos">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl">Your Videos</h2>
            <p className="text-muted-foreground text-sm">
              Upload videos or link to YouTube/Vimeo. Use arrows to reorder.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-foreground text-background hover:bg-foreground/90">
            <Plus className="h-4 w-4 mr-2" />
            Add Video
          </Button>
        </div>

        {/* Videos grid */}
        {videos.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-serif text-xl mb-2">No videos yet</h3>
              <p className="text-muted-foreground mb-6">Add your first video to showcase your performances</p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Video
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card key={video.id} className={`bg-card border-border overflow-hidden ${reordering ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="aspect-video relative bg-muted">
                  {/* Reorder buttons */}
                  <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-background/80 backdrop-blur-sm hover:bg-background"
                      onClick={() => moveVideo(video.id, 'up')}
                      disabled={index === 0 || reordering}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 bg-background/80 backdrop-blur-sm hover:bg-background"
                      onClick={() => moveVideo(video.id, 'down')}
                      disabled={index === videos.length - 1 || reordering}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* Order number */}
                  <div className="absolute top-2 left-12 z-10 bg-background/80 backdrop-blur-sm rounded px-2 py-1 text-xs font-medium">
                    #{index + 1}
                  </div>
                  {video.thumbnail ? (
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : getVideoSource(video) ? (
                    <video 
                      src={getVideoSource(video)} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-background/80 text-xs">
                      {video.duration}
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-serif text-lg mb-1 line-clamp-1">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(video)}>
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDelete(video)} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {selectedVideo ? 'Edit Video' : 'Add Video'}
              </DialogTitle>
              <DialogDescription>
                {selectedVideo ? 'Update your video details' : 'Upload a video or link to YouTube/Vimeo'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={handleChange('title')}
                  placeholder="Video title"
                  className="bg-secondary border-border"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={handleChange('description')}
                  placeholder="Brief description..."
                  rows={3}
                  className="bg-secondary border-border resize-none"
                />
              </div>

              {/* Video Upload */}
              <FileUpload
                type="video"
                label="Upload Video File"
                value={form.video_file}
                onChange={handleFileChange('video_file')}
                onUploadComplete={handleVideoUploadComplete}
              />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="video_url">YouTube / Vimeo URL</Label>
                <Input
                  id="video_url"
                  value={form.video_url}
                  onChange={handleChange('video_url')}
                  placeholder="https://youtube.com/watch?v=..."
                  className="bg-secondary border-border"
                />
                <p className="text-xs text-muted-foreground">
                  Paste a YouTube or Vimeo URL to embed
                </p>
              </div>

              {/* Thumbnail Upload */}
              <FileUpload
                type="image"
                label="Thumbnail Image"
                value={form.thumbnail}
                onChange={handleFileChange('thumbnail')}
              />

              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  value={form.duration}
                  onChange={handleChange('duration')}
                  placeholder="5:30"
                  className="bg-secondary border-border w-32"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                className="bg-foreground text-background hover:bg-foreground/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {selectedVideo ? 'Save Changes' : 'Add Video'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Video</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedVideo?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminVideos;
